const nf0 = new Intl.NumberFormat('uk-UA', { maximumFractionDigits: 0 });
const nf1 = new Intl.NumberFormat('uk-UA', { maximumFractionDigits: 1 });

/** Format integer with Ukrainian-style thin-space groupings. */
export function fmtInt(n: number): string {
  return nf0.format(Math.round(n));
}

/** Format a percentage value (already a percent, e.g. 18.4 → "18.4%"). */
export function fmtPct(n: number): string {
  return `${nf1.format(n)}%`;
}

/**
 * Format a signed delta percentage with a leading ▲/▼ and CSS class hint.
 * Returns `{ text, isPositive }` so the caller can color the chip.
 */
export function fmtDeltaPct(n: number): { text: string; isPositive: boolean } {
  if (n === 0) return { text: '0.0%', isPositive: true };
  const sign = n > 0 ? '▲' : '▼';
  return { text: `${sign} ${nf1.format(Math.abs(n))}%`, isPositive: n > 0 };
}

/**
 * Format UAH currency from cents. Shows whole-thousand chunks compactly:
 *   24_000 → "₴ 240"
 *   28_700_000 → "₴ 287 K"
 *   1_204_000_000 → "₴ 1.2 M"
 */
export function fmtUah(cents: number, opts: { compact?: boolean } = {}): string {
  const uah = cents / 100;
  if (!opts.compact || Math.abs(uah) < 1000) {
    return `₴ ${nf0.format(uah)}`;
  }
  if (Math.abs(uah) < 1_000_000) {
    return `₴ ${nf0.format(uah / 1000)} K`;
  }
  return `₴ ${nf1.format(uah / 1_000_000)} M`;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

/**
 * Relative time для дашбордів та активності: "4 хв тому", "2 год тому",
 * "вчора", "3 дні тому", далі — короткий uk-UA "29 трав.".
 */
export function fmtRelative(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'щойно';
  if (min < 60) return `${min} хв тому`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} год тому`;
  const day = Math.floor(hr / 24);
  if (day === 1) return 'вчора';
  if (day < 7) return `${day} дні тому`;
  return d.toLocaleDateString('uk-UA', { day: 'numeric', month: 'short' });
}

/** Конкретна дата у форматі `dd.mm.yyyy` — для таблиць та detail-views. */
export function fmtDate(iso: string): string {
  const d = new Date(iso);
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
}

/** Дата + час у форматі `dd.mm.yyyy HH:MM:SS` — для платежів. */
export function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  return (
    `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()} ` +
    `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`
  );
}
